/**
 * AusHires quote drawer — in-memory cart of equipment requests.
 *
 * State lives only on window.AusHiresQuote (no localStorage / cookies).
 * Items collected from any "data-quote-add" button on the page.
 *
 * Public API (window.AusHiresQuote):
 *   add(item)        — { id, handle, title, type, url, image, catCode } (qty defaults to 1)
 *   setQty(id, n)
 *   remove(id)
 *   clear()
 *   open() / close() / toggle()
 *   items()          — returns current array
 *   count()          — sum of quantities
 *   subscribe(fn)    — fn(state) called on any change; returns unsubscribe
 */
(function () {
  "use strict";

  var state = { items: [], open: false };
  var listeners = [];

  function emit() {
    listeners.slice().forEach(function (fn) {
      try { fn(state); } catch (e) { /* swallow */ }
    });
  }

  function findIndex(id) {
    for (var i = 0; i < state.items.length; i++) {
      if (state.items[i].id === id) return i;
    }
    return -1;
  }

  var Quote = {
    add: function (item) {
      if (!item || !item.id) return;
      var i = findIndex(item.id);
      if (i >= 0) {
        state.items[i].qty = (state.items[i].qty || 1) + 1;
      } else {
        state.items.push({
          id: String(item.id),
          handle: item.handle || "",
          title: item.title || "",
          type: item.type || "",
          url: item.url || "",
          image: item.image || "",
          catCode: item.catCode || "Placeholder Code",
          qty: 1,
        });
      }
      state.open = true;
      emit();
    },
    setQty: function (id, n) {
      var i = findIndex(id);
      if (i < 0) return;
      var v = parseInt(n, 10);
      if (!v || v < 1) v = 1;
      if (v > 99) v = 99;
      state.items[i].qty = v;
      emit();
    },
    remove: function (id) {
      var i = findIndex(id);
      if (i < 0) return;
      state.items.splice(i, 1);
      emit();
    },
    clear: function () { state.items = []; emit(); },
    open: function () { state.open = true; emit(); },
    close: function () { state.open = false; emit(); },
    toggle: function () { state.open = !state.open; emit(); },
    items: function () { return state.items.slice(); },
    count: function () {
      return state.items.reduce(function (sum, it) { return sum + (it.qty || 1); }, 0);
    },
    subscribe: function (fn) {
      listeners.push(fn);
      return function () {
        var i = listeners.indexOf(fn);
        if (i >= 0) listeners.splice(i, 1);
      };
    },
  };

  window.AusHiresQuote = Quote;

  // ---- DOM wiring ----------------------------------------------------------
  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    var categoryStrip = document.querySelector("[data-category-strip]");
    var categoryToggle = document.querySelector("[data-category-toggle]");
    var categoryForcedOpen = false;
    var lastScrollY = window.scrollY || 0;
    var manualCategoryOpenAt = 0;

    function updateCategoryStrip() {
      if (!categoryStrip) return;
      var atTop = (window.scrollY || 0) < 8;
      var shouldShow = atTop || categoryForcedOpen;
      categoryStrip.classList.toggle("is-hidden", !shouldShow);
      if (categoryToggle) categoryToggle.setAttribute("aria-expanded", shouldShow ? "true" : "false");
    }

    if (categoryStrip) {
      updateCategoryStrip();
      window.addEventListener("scroll", function () {
        var currentY = window.scrollY || 0;
        var atTop = currentY < 8;
        if (!atTop && Math.abs(currentY - lastScrollY) > 4 && Date.now() - manualCategoryOpenAt > 400) categoryForcedOpen = false;
        lastScrollY = currentY;
        updateCategoryStrip();
      }, { passive: true });
    }

    if (categoryToggle) {
      categoryToggle.addEventListener("click", function (ev) {
        ev.preventDefault();
        manualCategoryOpenAt = Date.now();
        categoryForcedOpen = true;
        updateCategoryStrip();
      });
    }

    // 1. Wire all "Add to quote" buttons
    document.addEventListener("click", function (ev) {
      var addBtn = ev.target.closest && ev.target.closest("[data-quote-add]");
      if (addBtn) {
        ev.preventDefault();
        Quote.add({
          id: addBtn.getAttribute("data-product-id"),
          handle: addBtn.getAttribute("data-product-handle"),
          title: addBtn.getAttribute("data-product-title"),
          type: addBtn.getAttribute("data-product-type"),
          url: addBtn.getAttribute("data-product-url"),
          image: addBtn.getAttribute("data-product-image"),
          catCode: addBtn.getAttribute("data-product-cat-code"),
        });
        return;
      }
      var openBtn = ev.target.closest && ev.target.closest("[data-quote-open]");
      if (openBtn) { ev.preventDefault(); Quote.open(); return; }
      var closeBtn = ev.target.closest && ev.target.closest("[data-quote-close]");
      if (closeBtn) { ev.preventDefault(); Quote.close(); return; }
    });

    var drawer = document.getElementById("ah-quote-drawer");
    if (!drawer) return;
    var emptyEl = drawer.querySelector("[data-quote-empty]");
    var itemsEl = drawer.querySelector("[data-quote-items]");
    var footEl = drawer.querySelector("[data-quote-foot]");
    var formEl = drawer.querySelector("[data-quote-form]");
    var successEl = drawer.querySelector("[data-quote-success]");
    var itemsField = drawer.querySelector("[data-quote-items-field]");

    function renderItems() {
      itemsEl.innerHTML = "";
      state.items.forEach(function (it) {
        var li = document.createElement("li");
        li.setAttribute("data-quote-row", it.id);
        var media = it.image
          ? '<img class="ah-drawer-item-image" src="' + escapeHtml(it.image) + '" alt="' + escapeHtml(it.title) + '" loading="lazy" />'
          : '<div class="ah-drawer-item-image ah-drawer-item-image--placeholder" aria-hidden="true"><svg viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg"><rect width="120" height="90" fill="#FFFFFF"/><polygon points="20,70 60,30 100,70" fill="#FFF200" stroke="#0F1B2D" stroke-width="2"/><rect x="0" y="74" width="120" height="3" fill="#00B7E2"/></svg></div>';
        li.innerHTML =
          media +
          '<div class="ah-drawer-item-info">' +
            '<strong>' + escapeHtml(it.title) + '</strong>' +
            (it.type ? '<span>' + escapeHtml(it.type) + '</span>' : '') +
            '<small>Cat Code: ' + escapeHtml(it.catCode || "Placeholder Code") + '</small>' +
          '</div>' +
          '<div class="ah-drawer-qty">' +
            '<button type="button" data-quote-dec aria-label="Decrease">−</button>' +
            '<input type="number" min="1" max="99" value="' + (it.qty || 1) + '" data-quote-input />' +
            '<button type="button" data-quote-inc aria-label="Increase">+</button>' +
          '</div>' +
          '<button type="button" class="ah-drawer-remove" data-quote-remove aria-label="Remove">×</button>';

        li.addEventListener("click", function (ev) {
          if (ev.target.matches("[data-quote-inc]"))
            Quote.setQty(it.id, (Quote._lookup(it.id) || 1) + 1);
          else if (ev.target.matches("[data-quote-dec]"))
            Quote.setQty(it.id, Math.max(1, (Quote._lookup(it.id) || 1) - 1));
          else if (ev.target.matches("[data-quote-remove]"))
            Quote.remove(it.id);
        });
        li.querySelector("[data-quote-input]").addEventListener("change", function (ev) {
          Quote.setQty(it.id, ev.target.value);
        });
        itemsEl.appendChild(li);
      });
    }

    Quote._lookup = function (id) {
      var i = findIndex(id);
      return i >= 0 ? state.items[i].qty : 0;
    };

    function renderCounts() {
      var c = Quote.count();
      document.querySelectorAll("[data-quote-count]").forEach(function (el) {
        el.textContent = String(c);
      });
    }

    function renderDrawer() {
      drawer.hidden = !state.open;
      var empty = state.items.length === 0;
      if (emptyEl) emptyEl.hidden = !empty;
      if (itemsEl) itemsEl.hidden = empty;
      if (footEl) footEl.hidden = empty;
    }

    function syncItemsField() {
      if (!itemsField) return;
      itemsField.value = state.items
        .map(function (it) { return it.qty + " × " + it.title + " | Cat Code: " + (it.catCode || "Placeholder Code") + (it.handle ? " [" + it.handle + "]" : ""); })
        .join("\n");
    }

    Quote.subscribe(function () {
      renderItems();
      renderCounts();
      renderDrawer();
      syncItemsField();
    });

    // initial render
    renderItems(); renderCounts(); renderDrawer(); syncItemsField();

    // Form submit — when integration is "app_proxy" or "klaviyo", intercept
    // and POST JSON to settings.quote_endpoint. Otherwise, let the form post
    // to the contact form action.
    if (formEl) {
      formEl.addEventListener("submit", function (ev) {
        var integration = (window.AusHiresThemeSettings && window.AusHiresThemeSettings.quote_integration) || "contact_form";
        if (integration === "app_proxy" || integration === "klaviyo") {
          ev.preventDefault();
          var endpoint = formEl.getAttribute("action");
          var data = new FormData(formEl);
          var payload = {
            integration: integration,
            contact: {
              name: data.get("contact[name]"),
              email: data.get("contact[email]"),
              phone: data.get("contact[phone]"),
              project: data.get("contact[project]"),
              body: data.get("contact[body]"),
            },
            items: state.items,
          };
          fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }).then(function () { showSuccess(); })
            .catch(function () { showSuccess(); /* still show; user already submitted */ });
        } else {
          // Default: contact_form. Let Shopify handle the POST; flip success
          // state immediately for snappy UX, then submit normally.
          showSuccess();
        }
      });
    }

    function showSuccess() {
      if (formEl) formEl.hidden = true;
      if (successEl) successEl.hidden = false;
      Quote.clear();
    }

    function escapeHtml(s) {
      return String(s == null ? "" : s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    }

    // Esc closes drawer
    document.addEventListener("keydown", function (ev) {
      if (ev.key === "Escape" && state.open) Quote.close();
    });
  });
})();
